import _ from '../../../utils/underscore';
import DOM from '../../../utils/dom';
import Event from '../../../utils/event';
import Device from '../../../utils/device/device';
import BaseObject from '../../../base/base_object';
import ServiceFactory from '../../service_factory';
import {TOAST_TYPES, POSITION, DEFAULT_CONFIG, TOASTER_UI_CONFIG} from '../toaster_service_consts';
import {SYSTEM_EVENTS} from '../../../events/events';

class ToasterFactory extends BaseObject {
    static CLASS = "ToasterFactory";

	/**
	 * Toaster "history" mechanism in case the refference to a toaster opetions object is lost
	 * @type {{}}
	 * @private
	 */
	_configs = {};


	/**
	 * Returns a toaster configuration with toasterID
	 * @param toasterID
	 * @returns {*}
	 */
	getToasterConfiguration(toasterID) {
		return this._configs[toasterID];
	}


	/**
	 * Removes a toaster configuration with toasterID
	 * @param toasterID
	 * @returns {*}
	 */
	removeToasterConfiguration(toasterID) {
		delete this._configs[toasterID];
	}


	/**
	 * Creates the iziToast options object according to the toaster type determined by toasterData
	 * @param toasterData
	 * @returns Object
	 */
	buildToasterConfiguration(toasterData) {
		let toasterType = this._determineToasterType(toasterData);
		let toasterConfig;

		switch (toasterType) {
			case TOAST_TYPES.ACKNOWLEDGE:
				toasterConfig = this._buildAckToasterConfig(toasterData, Device.isMobile());
				break;
			case TOAST_TYPES.ACTION:
				toasterConfig = this._buildActionToasterConfig(toasterData, Device.isMobile());
				break;
			default:
				toasterConfig = this._buildTimedToasterConfig(toasterData, Device.isMobile());
				break;
		}

		if(toasterConfig.timeout) {
			toasterConfig = this._addTimerToToaster(toasterConfig);
		}

		toasterConfig.type = toasterType;
		this._configs[toasterConfig.id] = toasterConfig;

		return toasterConfig;
	}


	// ====================================== TEMPORARY TOASTERS ======================================

	_addTimerToToaster(toasterData) {
		let toasterUICfg = Object.assign({}, TOASTER_UI_CONFIG, toasterData.customUIConfig),
			themesService = ServiceFactory.create().themesService();


		return Object.assign({}, toasterData, {
			progressBar: true,
			progressBarColor: themesService.getColors()[toasterUICfg.progressColor],
		});
	}

	/**
	 * Builds the timed toaster options object for iziToast
	 * @param toasterData
	 * @param isMobile
	 * @returns Object
	 * @private
	 */
	_buildTimedToasterConfig(toasterData, isMobile) {
		let toasterTypeConfig = this._createToasterTypeConfig(isMobile, toasterData.image);
		return Object.assign({}, DEFAULT_CONFIG, toasterTypeConfig, toasterData);
	}


	/**
	 * Applies timed toaster css / style properties on the toaster DOM element
	 * @param toasterHTML
	 * @param themesService
	 * @private
	 */
	_applyTimedToasterDomConfig(toasterHTML, themesService) {
		const domElements = this._getToasterTextElements(toasterHTML),
			toasterUIConfig = TOASTER_UI_CONFIG;

		DOM.addStyle(domElements.title, 'font-family', themesService.getFonts()[toasterUIConfig.titleFont]);
		DOM.addStyle(domElements.message, 'font-family', themesService.getFonts()[toasterUIConfig.messageFont]);
		DOM.addStyle(domElements.message, 'margin-bottom', 0);
	}


	// ====================================== ACKNOWLEDGE TOASTERS ======================================


	/**
	 * Builds the acknowledge toaster options object for iziToast
	 * @param toasterData
	 * @param isMobile
	 * @returns Object
	 * @private
	 */
	_buildAckToasterConfig(toasterData, isMobile) {
		let toasterTypeConfig = this._createToasterTypeConfig(isMobile, toasterData.image);
		const themesService = ServiceFactory.create().themesService(),
			toasterOkBtn = DOM.createElement('button', ServiceFactory.create().languageService().translation('toaster_ok')),
			userCloseFunction = (iziToastLib, toasterHTML) => {
				let toasterService = ServiceFactory.create().toasterService();
				this._sendBtnClickedAnalytics(toasterData);
				Event.fire(SYSTEM_EVENTS.TOAST_USER_DISMISS, {toast: toasterHTML});
				toasterService.hideToast(toasterHTML, toasterTypeConfig);
			};

		// configure toaster ok button
		DOM.addStyle(toasterOkBtn, 'color', themesService.getColors()[TOASTER_UI_CONFIG.buttonTextColor])
			.addStyle(toasterOkBtn,'font-family', themesService.getFonts()[TOASTER_UI_CONFIG.buttonFont])
			.addStyle(toasterOkBtn,'border-radius', themesService.getUserPreferences()[TOASTER_UI_CONFIG.buttonCornerRadius])
			.addStyle(toasterOkBtn,'font-weight', "bold")
			.addStyle(toasterOkBtn, 'background-color', themesService.getColors()[TOASTER_UI_CONFIG.buttonColor])
			.addAttribute(toasterOkBtn, 'onMouseOut',
				'this.style.backgroundColor = \'' + themesService.getColors()[TOASTER_UI_CONFIG.buttonColor] + '\''
			);


		toasterTypeConfig = Object.assign({}, DEFAULT_CONFIG, toasterTypeConfig, {
			buttons: [
				[toasterOkBtn.outerHTML, userCloseFunction]
			]
		}, toasterData);

		return toasterTypeConfig;
	}

	/**
	 * Applies acknowledge toaster css / style properties on the toaster DOM element
	 * @param toasterHTML
	 * @param themesService
	 * @private
	 */
	_applyAckToasterDomConfig(toasterHTML, themesService) {
		let domElements = this._getToasterTextElements(toasterHTML),
			toasterUIConfig = TOASTER_UI_CONFIG;
		DOM.addStyle(domElements.title, 'font-family', themesService.getFonts()[toasterUIConfig.titleFont]);
		DOM.addStyle(domElements.message, 'font-family', themesService.getFonts()[toasterUIConfig.messageFont]);
		DOM.removeStyle(toasterHTML, 'padding-right');
	}


	// ====================================== GAME TOASTERS ======================================


	/**
	 * Builds the game toaster options object for iziToast
	 * @param toasterData
	 * @param isMobile
	 * @returns Object
	 * @private
	 */
	_buildActionToasterConfig(toasterData, isMobile) {
		let toasterTypeConfig = this._createToasterTypeConfig(isMobile, toasterData.image),
			themesService = ServiceFactory.create().themesService();

		let actionBtn = DOM.createElement('button', toasterData.buttonText);

		// configure toaster action button
		DOM.addStyle(actionBtn, 'color', themesService.getColors()[TOASTER_UI_CONFIG.buttonTextColor])
			.addStyle(actionBtn,'font-family', themesService.getFonts()[TOASTER_UI_CONFIG.buttonFont])
			.addStyle(actionBtn,'padding', "8px 60px")
			.addStyle(actionBtn,'border-radius', themesService.getUserPreferences()[TOASTER_UI_CONFIG.buttonCornerRadius])
			.addStyle(actionBtn,'font-weight', "bold")
			.addStyle(actionBtn, 'background-color', themesService.getColors()[TOASTER_UI_CONFIG.buttonColor])
			.addAttribute(actionBtn, 'onMouseOut',
				'this.style.backgroundColor = \'' + themesService.getColors()[TOASTER_UI_CONFIG.buttonColor] + '\''
			);

		toasterTypeConfig = Object.assign({}, DEFAULT_CONFIG, toasterTypeConfig, {
			buttons: [
				[actionBtn.outerHTML, (iziToastLib, toasterHTML) => {
					this._sendBtnClickedAnalytics(toasterData);
					ServiceFactory.create().toasterService().hideToast(toasterHTML, toasterTypeConfig);
					toasterData.callback();
				}]
			],
			layout: 2,
		},toasterData);
		return toasterTypeConfig;
	}


	_createToasterTypeConfig(isMobile, image) {
		const toasterUICfg = TOASTER_UI_CONFIG;
		const themesService = ServiceFactory.create().themesService();
        const languageService = ServiceFactory.create().languageService();

		return {
			class: toasterUICfg.globalClass,
			titleSize: themesService.getFontSizes()[toasterUICfg.titleSize],
			titleColor: themesService.getColors()[toasterUICfg.titleColor],
			messageSize: themesService.getFontSizes()[toasterUICfg.messageSize],
			messageColor: themesService.getColors()[toasterUICfg.messageColor],
			backgroundColor: themesService.getColors()[toasterUICfg.backgroundColor],
			drag: isMobile,
			cssImage: image ? null : ['cgp-general-notification-toaster-icon'],
			image: image ? image : 'TBA',
			titleLineHeight: '20',
			close: !isMobile,
			progressBar: false,
			position: isMobile ? POSITION.TOP_CENTER : (languageService.getIsRTL()? POSITION.BOTTOM_LEFT : POSITION.BOTTOM_RIGHT),
			maxWidth: isMobile? null : '360px',
			applyDomConfig: this._applyGameToasterDomConfig,
            rtl: languageService.getIsRTL()
		};
	}

	/**
	 * Applies game toaster css / style properties on the toaster DOM element
	 * @param toasterHTML
	 * @param themesService
	 * @private
	 */
	_applyGameToasterDomConfig(toasterHTML, themesService) {
		let domElements = this._getToasterTextElements(toasterHTML),
			toasterUIConfig = TOASTER_UI_CONFIG;
		DOM.addStyle(domElements.title, 'font-family', themesService.getFonts()[toasterUIConfig.titleFont]);
		DOM.addStyle(domElements.message, 'font-family', themesService.getFonts()[toasterUIConfig.messageFont]);
		DOM.addStyle(domElements.message, 'margin-bottom', 0);
	}


	/**
	 * Returns the type of the toaster to be created
	 * @param data
	 * @returns {string}
	 * @private
	 */
	_determineToasterType(data) {
		if (data.type) {
			return data.type;
		}

		if (data.layout === 2) {
			return TOAST_TYPES.ACTION;
		}

		if (_.isNullOrUndefined(data.timeout) || data.timeout === 0) {
			return TOAST_TYPES.ACKNOWLEDGE;
		}

		return TOAST_TYPES.TIMED;
	}

	_sendBtnClickedAnalytics(toasterData) {
		const cid = ServiceFactory.create().userService().getCID() || 'annonymous';

		const analyticsObj = {
			category: 'Communications',
			action:  toasterData.id + '_CTA_Click',
			label : cid + '_' + Date.now().toString()
		}

		Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticsObj);
	}

	/**
	 * Returns refferences to the toaster text elements ( title + message)
	 * @param toasterHTML
	 * @returns object
	 * @private
	 */
	_getToasterTextElements(toasterHTML) {
		let textContainer = toasterHTML.children[0].children[0];
		return {
			title: textContainer.children[0],
			message: textContainer.children[1]
		};
	}
}

export default ToasterFactory;